import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

import { computed, ref } from 'vue'

import BasePopup from '@/components/ui/BasePopup.vue'
import CancelFlowIntro from './CancelFlowIntro.vue'
import CancelFlowReasons from './CancelFlowReasons.vue'
import CancelFlowRelevantBooks from './CancelFlowRelevantBooks.vue'
import CancelFlowPauseFeature from './CancelFlowPauseFeature.vue'
import CancelFlowFeedback from './CancelFlowFeedback.vue'
import CancelFlowDiscount from './CancelFlowDiscount.vue'

import { BillingView } from '@/models/billing'
import type { ReasonValue } from '@/models/billing.cancelFlow'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { Mixpanel } from '@/analytics/mixpanel'

type Step = 'intro' | 'discount' | 'reasons' | 'feature' | 'books' | 'feedback'

// The follow-up step for a reason; reasons without one cancel directly.

export default /*@__PURE__*/_defineComponent({
  __name: 'CancelFlowPopup',
  props: {
    billing: {}
  },
  setup(__props: any, { expose: __expose }) {

const REASON_STEPS: Partial<Record<ReasonValue, Step>> = {
  not_finding_time: 'feature',
  not_enough_books: 'books',
  content_short: 'feedback',
  app_short: 'feedback',
  something_else: 'feedback',
}

const props = __props

const popup = ref<InstanceType<typeof BasePopup> | null>(null)
const step = ref<Step>('intro')
const canceling = ref(false)
const registering = ref(false)
const selectedReason = ref<ReasonValue | null>(null)
// The id of the answer saved on the reason click (null if the save failed).
let savedAnswer: Promise<string | null> | null = null

const title = computed(() => {
  switch (step.value) {
    case 'intro':
      return 'Are you sure you want to end your subscription?'
    case 'reasons':
      return 'Before you go,<br>can you tell us why?'
    case 'feedback':
      return 'What could we do better?'
    // The discount, pause and books steps render their own heading.
    default:
      return ''
  }
})

function show(): void {
  step.value = 'intro'
  selectedReason.value = null
  savedAnswer = null
  popup.value?.show()
}

function hide(): void {
  popup.value?.hide(false)
}

// Track when the user dismisses the flow (X or click outside). Programmatic
// closes pass trackEvent=false, so they don't fire this.
function onHide(trackEvent: boolean): void {
  if (trackEvent) {
    Mixpanel.trackCancelFlowClosed(
      props.billing,
      selectedReason.value,
      step.value,
    )
  }
}

/**
 * Register the cancel discount. The backend registers it only for users who
 * can get it (trial Stripe users without another discount), everyone else
 * goes to the reasons step.
 */
async function onIntroContinue(): Promise<void> {
  try {
    registering.value = true
    const registered = await backend.registerCancelDiscount()
    step.value = registered ? 'discount' : 'reasons'
  } finally {
    registering.value = false
  }
}

/**
 * Save the reason right away, so it isn't lost if the user leaves the
 * follow-up step without answering, then open that step or cancel.
 */
function onReasonSelected(reason: ReasonValue): void {
  selectedReason.value = reason
  savedAnswer = backend.saveCancelFlowAnswer(reason).catch((err) => {
    Sentry.captureException(err)
    return null
  })

  const nextStep = REASON_STEPS[reason]
  Mixpanel.trackCancelReasonSubmitted(
    props.billing,
    reason,
    nextStep ?? 'cancel',
  )
  if (nextStep) {
    step.value = nextStep
  } else {
    onContinueCanceling()
  }
}

// A failing save shouldn't block the cancellation.
async function onFeedback(feedback: string): Promise<void> {
  await Promise.all([saveFeedback(feedback), onContinueCanceling()])
}

async function saveFeedback(feedback: string): Promise<void> {
  const reason = selectedReason.value
  if (!reason || !feedback) {
    return
  }
  try {
    const answerId = await savedAnswer
    await backend.saveCancelFlowAnswer(reason, feedback, answerId ?? undefined)
  } catch (err) {
    Sentry.captureException(err)
  }
}

/**
 * End the subscription (switch to the free plan). Closes the popup only on
 * success, so a failure keeps it open for a retry.
 */
async function onContinueCanceling(): Promise<void> {
  try {
    canceling.value = true
    await props.billing.subscribeTo(props.billing.freePlan)
    hide()
  } catch (err) {
    Sentry.captureException(err)
    alert('Failed to cancel the subscription')
  } finally {
    canceling.value = false
  }
}

__expose({ show, hide })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BasePopup, {
    ref_key: "popup",
    ref: popup,
    title: title.value,
    class: "cancel-flow-popup",
    onHide: onHide
  }, {
    default: _withCtx(() => [
      (step.value === 'intro')
        ? (_openBlock(), _createBlock(CancelFlowIntro, {
            key: 0,
            billing: __props.billing,
            loading: registering.value,
            onKeep: hide,
            onContinue: onIntroContinue
          }, null, 8, ["billing", "loading"]))
        : (step.value === 'discount')
          ? (_openBlock(), _createBlock(CancelFlowDiscount, {
              key: 1,
              billing: __props.billing,
              onAccepted: hide,
              onDecline: _cache[0] || (_cache[0] = ($event: any) => (step.value = 'reasons'))
            }, null, 8, ["billing"]))
          : (step.value === 'reasons')
            ? (_openBlock(), _createBlock(CancelFlowReasons, {
                key: 2,
                billing: __props.billing,
                canceling: canceling.value,
                onReasonSelected: onReasonSelected,
                onContinueCancel: onContinueCanceling
              }, null, 8, ["billing", "canceling"]))
            : (step.value === 'feature')
              ? (_openBlock(), _createBlock(CancelFlowPauseFeature, {
                  key: 3,
                  billing: __props.billing,
                  canceling: canceling.value,
                  reason: selectedReason.value,
                  onFeedback: onFeedback,
                  onContinueCancel: onContinueCanceling
                }, null, 8, ["billing", "canceling", "reason"]))
              : (step.value === 'books')
                ? (_openBlock(), _createBlock(CancelFlowRelevantBooks, {
                    key: 4,
                    billing: __props.billing,
                    canceling: canceling.value,
                    reason: selectedReason.value,
                    onContinueCancel: onContinueCanceling
                  }, null, 8, ["billing", "canceling", "reason"]))
                : (_openBlock(), _createBlock(CancelFlowFeedback, {
                    key: 5,
                    billing: __props.billing,
                    canceling: canceling.value,
                    reason: selectedReason.value,
                    onSend: onFeedback,
                    onContinueCancel: onContinueCanceling
                  }, null, 8, ["billing", "canceling", "reason"]))
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})